import classNames from 'classnames';
import React from 'react';
import _ from 'underscore';

import * as benchlingUtil from 'BenchlingUtil';
import {SchemaTag} from 'folder-items/FileConstants';
import InsertLinkModalClass from 'notebook/mediocre/hyperlinks/InsertLinkModal';
import {InternalLink} from 'notebook/mediocre/MediocreConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import FormInput from 'ui-library/bs/FormInput';
import Tooltip from 'ui-library/bs/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import ModalAnchor from 'ui-library/ModalAnchor';
import StrUtil from 'util/strUtil';
import {InputElementFocusEvent, ElementKeyboardEvent, InputChangeEvent} from 'util/Types';

export interface TextAndUrlValueEditorProps {
  autoFocus: boolean;
  defaultValue: SchemaTag;
  fileId?: string;
  itemLinkAllowed: boolean;
  onChange: (state: {value: string; url: string}) => void;
  onKeyDown?: (e: ElementKeyboardEvent) => void;
  placeholder?: string;
  isDisabled?: boolean;
}

interface TextAndUrlValueEditorState extends TextAndUrlValueEditorProps {
  showInsertLinkModal: boolean;
  value?: string;
  url: string;
}

export default class TextAndUrlValueEditor extends React.Component<
  TextAndUrlValueEditorProps,
  TextAndUrlValueEditorState
> {
  static defaultProps = {
    autoFocus: false,
    itemLinkAllowed: true,
    placeholder: 'Value',
  };

  state: TextAndUrlValueEditorState = {
    ..._.defaults({}, this.props.defaultValue || {}, {value: '', url: ''}),
    showInsertLinkModal: false,
  };

  UNSAFE_componentWillReceiveProps(nextProps: TextAndUrlValueEditorProps): void {
    if (
      nextProps.defaultValue.value != null &&
      nextProps.defaultValue.value !== this.props.defaultValue.value
    ) {
      this.setState({value: nextProps.defaultValue.value});
    }
  }

  private closeInsertLinkModal = (): void => {
    this.setState({showInsertLinkModal: false});
  };

  private setLinkAndClose = (link: {text: string; url: string | null} | InternalLink | null): void => {
    if (link != null) {
      let url = '';
      if (link.url != null) {
        const urlComponents = benchlingUtil.parseUri(link.url);
        url = urlComponents.path === link.url ? benchlingUtil.pathToURL(link.url) : link.url;
      }

      const newState = {
        value: this.isInternalLink(link) ? link.name : link.text,
        url,
      };
      this.setState(newState);
      this.props.onChange(newState);
    }
    this.closeInsertLinkModal();
  };

  private renderEditLinkModal = (): JSX.Element => (
    <ModalAnchor open={this.state.showInsertLinkModal}>
      <InsertLinkModalClass
        fileId={this.props.fileId}
        open={this.state.showInsertLinkModal}
        initialText={this.state.value}
        initialUrl={this.state.url}
        isExisting={StrUtil.isValidUrl(this.state.url)}
        setLinkAndClose={this.setLinkAndClose}
      />
    </ModalAnchor>
  );

  private handleLinkClicked = (): void => {
    this.setState({showInsertLinkModal: true});
  };

  private handleInputChange = (e: InputChangeEvent): void => {
    const newValue = e.target.value;
    this.setState({value: newValue});
    this.props.onChange({
      value: newValue,
      url: this.state.url,
    });
  };

  private isInternalLink(
    link: {text: string; url: string | null} | InternalLink | null
  ): link is InternalLink {
    return (link as InternalLink).name !== undefined;
  }

  render(): JSX.Element {
    const linkClassName = classNames('metadata-action-icon--link', {
      inactive: !StrUtil.isValidUrl(this.state.url),
    });

    return (
      <div className="u-position-relative u-flex-grow">
        {this.props.itemLinkAllowed && (
          <div>
            {this.renderEditLinkModal()}
            <Tooltip container="body" title="Link File">
              <a onClick={this.handleLinkClicked} className="metadata-tagValueInputLink">
                <BenchlingIcon icon="link" className={linkClassName} />
              </a>
            </Tooltip>
          </div>
        )}
        <FormInput
          disabled={this.props.isDisabled}
          autoFocus={this.props.autoFocus}
          onFocus={(e: InputElementFocusEvent) => {
            const input = e.target;
            input.setSelectionRange(0, input.value.length);
          }}
          onChange={this.handleInputChange}
          onKeyDown={this.props.onKeyDown}
          placeholder={this.props.placeholder}
          value={this.state.value}
          {...getTestProps({
            component: 'TextAndUrlValueEditor',
            element: 'FormInput',
          })}
        />
      </div>
    );
  }
}
